import React, { useRef, useLayoutEffect } from "react";
import { PropTypes } from "prop-types";
import { observer } from "mobx-react";
import { MrbPreviewPage, MrbPageContentHeader, MrbPageHeader } from "mrb/components/layout";
import { defaultTemplate } from "common/hoc";
import { Image, StatusTag, Popover } from "common/components";
import {
    PodcastEpisode,
    PodcastEpisodeListLoader,
    PodcastEpisodeEditTitleModal,
} from "administration/podcast/components";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import { MrbContentLoader, MrbHeaderLoader } from "mrb/components/loader";
import { VariableSizeList } from "react-window";
import { MrbButton } from "mrb/components/button";
import { GenerateAudioFilesModal } from "administration/content-sync/components";
import { isNil, isEmpty } from "lodash";
import { MrbRadioButtons } from "mrb/components/radio";
import { EmptyState } from "common/components/empty-state";

function PodcastEpisodeListTemplate({ podcastEpisodeListViewStore, t }) {
    const {
        openGeneratePopupForPodcast,
        item,
        coverImageUrl,
        isSyncing,
        openGeneratePopupForEpisode,
        goToUploadEpisode,
        generateAudioFilesModal,
        loaderStore,
        episodes,
        filteredEpisodes,
        filter,
        syncNewEpisodes,
        editPodcastEpisodeTitleModal,
        openEditPodcastEpisodeTitleModal,
        downloadPodcastEpisode,
        deletePodcastEpisode,
        toggleEpisodeActive,
        setEpisodeScheduledRelease,
    } = podcastEpisodeListViewStore;

    const listRef = useRef(null);
    const sizeKey = filteredEpisodes.map((e) => e.isActive).join(",");
    useLayoutEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [sizeKey]);

    return (
        <MrbPreviewPage store={podcastEpisodeListViewStore} contentClassNameExtend="u-padd--none" t={t}>
            <MrbPageHeader>
                <div className="c-page__header--top">
                    {item ? (
                        <React.Fragment>
                            {isNil(item.parentPodcastId) ? (
                                <MrbRouterLink
                                    routeName="master.administration.podcast.preview"
                                    params={{ id: item.id }}
                                >
                                    <span className="display--ib u-mar--right--tny align--v--middle">
                                        <i className="u-icon u-icon--med u-icon--back" />
                                    </span>
                                    {item.title}
                                </MrbRouterLink>
                            ) : (
                                <MrbRouterLink
                                    routeName="master.administration.podcast.preview-group"
                                    params={{ id: item.parentPodcastId }}
                                >
                                    <span className="display--ib u-mar--right--tny align--v--middle">
                                        <i className="u-icon u-icon--med u-icon--back" />
                                    </span>
                                    {item.parentPodcast.title}
                                </MrbRouterLink>
                            )}
                            <div className="c-header__actions c-header__actions--end">
                                {isSyncing ? (
                                    <div className="c-validation--error">
                                        <p className="c-validation__label">
                                            {t("ADMINISTRATION.PODCAST.EPISODES.HEADER.SYNC_MESSAGE")}
                                        </p>
                                    </div>
                                ) : null}
                                {item.isActive && (
                                    <Popover
                                        contentClassName="c-popover__content--right"
                                        trigger={
                                            <MrbButton
                                                label={t("ADMINISTRATION.PODCAST.EPISODES.HEADER.BUTTONS.ACTIONS")}
                                            />
                                        }
                                        contentList={
                                            item.podcastFeed.hlsEnabled
                                                ? [
                                                      {
                                                          title: t(
                                                              "ADMINISTRATION.PODCAST.EPISODES.HEADER.BUTTONS.GENERATE"
                                                          ),
                                                          action: openGeneratePopupForPodcast,
                                                          isDisabled: isSyncing,
                                                      },
                                                      {
                                                          title: t(
                                                              "ADMINISTRATION.PODCAST.EPISODES.HEADER.BUTTONS.SYNC_NEW_EPISODES"
                                                          ),
                                                          action: syncNewEpisodes,
                                                          isDisabled: isSyncing,
                                                      },
                                                  ]
                                                : [
                                                      {
                                                          title: t(
                                                              "ADMINISTRATION.PODCAST.EPISODES.HEADER.BUTTONS.SYNC_NEW_EPISODES"
                                                          ),
                                                          action: syncNewEpisodes,
                                                          isDisabled: isSyncing,
                                                      },
                                                  ]
                                        }
                                    />
                                )}
                                <MrbButton
                                    label="ADMINISTRATION.PODCAST.EPISODES.HEADER.BUTTONS.ADD_EPISODE"
                                    t={t}
                                    onClick={goToUploadEpisode}
                                />
                            </div>
                        </React.Fragment>
                    ) : null}
                </div>
            </MrbPageHeader>
            <MrbPageContentHeader classNameOverride="c-content__header--primary">
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <MrbHeaderLoader />}>
                    {() => (
                        <React.Fragment>
                            <div className="c-content__header--primary__img">
                                <Image src={coverImageUrl} />
                            </div>
                            <div className="c-content__header--primary__info">
                                <div className="u-mar--bottom--sml">
                                    <p className="u-type--sml u-type--color--opaque">
                                        {t("ADMINISTRATION.PODCAST.EPISODES.HEADER.TITLE")}
                                    </p>
                                    <div className="u-display--flex u-display--flex--ai--center u-gap--base">
                                        <p className="c-content__header--primary__title">{item.title}</p>

                                        <StatusTag isActive={item.isActive} />
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </MrbContentLoader>
            </MrbPageContentHeader>

            <div className="c-container c-container--base">
                <MrbContentLoader loaderStore={loaderStore} loaderRender={() => <PodcastEpisodeListLoader />}>
                    {() => (
                        <React.Fragment>
                            {!isEmpty(episodes) && (
                                <div className="u-animation--fadeIn">
                                    <EpisodeFilter filter={filter} t={t} />
                                </div>
                            )}
                            {!isEmpty(episodes) && isEmpty(filteredEpisodes) ? (
                                <div className="c-card c-card--primary c-card--med">
                                    <EmptyState />
                                </div>
                            ) : (
                                <VariableSizeList
                                    ref={listRef}
                                    height={!isEmpty(episodes) ? 500 : 660}
                                    width={"100%"}
                                    itemCount={filteredEpisodes.length}
                                    itemSize={(index) => {
                                        const baseSize = item.isActive && item.podcastFeed.hlsEnabled ? 396 : 361;
                                        return filteredEpisodes[index].isActive === false ? baseSize + 91 : baseSize;
                                    }}
                                    style={{ overflowX: "hidden" }}
                                    itemData={{
                                        items: filteredEpisodes,
                                        store: podcastEpisodeListViewStore,
                                    }}
                                >
                                    {({ index, style }) => (
                                        <div style={style}>
                                            <PodcastEpisode
                                                item={filteredEpisodes[index]}
                                                wrapperClassName="u-mar--right--sml u-mar--bottom--med"
                                                t={t}
                                                isSyncing={isSyncing}
                                                generateAudioFiles={openGeneratePopupForEpisode}
                                                isActive={item.isActive}
                                                isHlsEnabled={item.podcastFeed.hlsEnabled}
                                                openEditPodcastEpisodeTitleModal={openEditPodcastEpisodeTitleModal}
                                                downloadPodcastEpisode={downloadPodcastEpisode}
                                                deletePodcastEpisode={deletePodcastEpisode}
                                                onToggleActive={toggleEpisodeActive}
                                                onSetScheduledRelease={setEpisodeScheduledRelease}
                                            />
                                        </div>
                                    )}
                                </VariableSizeList>
                            )}
                        </React.Fragment>
                    )}
                </MrbContentLoader>
            </div>
            <GenerateAudioFilesModal modalParams={generateAudioFilesModal} t={t} />
            <PodcastEpisodeEditTitleModal
                podcastEpisodeListViewStore={podcastEpisodeListViewStore}
                modalParams={editPodcastEpisodeTitleModal}
                t={t}
            />
        </MrbPreviewPage>
    );
}

const EpisodeFilter = observer(function EpisodeFilter({ filter, t }) {
    const { setSearch, setHlsAvailable, setMp3Available } = filter;

    const onChange = (event) => {
        setSearch(event.target.value);
    };

    const onInlineReset = () => {
        setSearch("");
    };

    const isSearchEmpty = isEmpty(filter.search);

    return (
        <div className="c-card c-card--primary u-mar--bottom--med">
            <div className="row">
                <div className="col col-sml-12 col-lrg-6">
                    <div className="u-position--rel u-mar--bottom--sml">
                        <input
                            className={`c-input c-input--search c-input--search--sml c-input--full c-input--base ${
                                !isSearchEmpty ? "clear" : ""
                            }`}
                            type="text"
                            placeholder={t("ADMINISTRATION.PODCAST.EPISODES.FILTER.SEARCH")}
                            value={filter.search || ""}
                            onChange={onChange}
                        />
                        {!isSearchEmpty && (
                            <span className="c-btn--clear" onClick={onInlineReset}>
                                <i className="u-icon u-icon--base u-icon--close--primary"></i>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="u-display--flex u-gap--med">
                <div className="u-display--flex u-display--flex--column u-display--flex--1">
                    <label className="c-form__field__label">
                        <div className="u-display--flex u-display--flex--jc--space-between">
                            <div>{t("ADMINISTRATION.PODCAST.EPISODES.FILTER.HLS_LABEL")}</div>
                        </div>
                    </label>
                    <MrbRadioButtons
                        id={`hlsAvailability`}
                        name={`hlsAvailability`}
                        options={[
                            { value: null, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_ALL") },
                            { value: true, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_AVAILABLE") },
                            { value: false, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_NOT_AVAILABLE") },
                        ]}
                        t={t}
                        value={filter.hlsAvailable}
                        classNameExtend={"c-segmented--full"}
                        onChange={(value) => setHlsAvailable(value)}
                    />
                </div>
                <div className="u-display--flex u-display--flex--column u-display--flex--1">
                    <label className="c-form__field__label">
                        <div className="u-display--flex u-display--flex--jc--space-between">
                            <div>{t("ADMINISTRATION.PODCAST.EPISODES.FILTER.MP3_LABEL")}</div>
                        </div>
                    </label>
                    <MrbRadioButtons
                        id={`mp3Availability`}
                        name={`mp3Availability`}
                        options={[
                            { value: null, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_ALL") },
                            { value: true, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_AVAILABLE") },
                            { value: false, label: t("ADMINISTRATION.PODCAST.EPISODES.FILTER.OPTION_NOT_AVAILABLE") },
                        ]}
                        t={t}
                        value={filter.mp3Available}
                        classNameExtend={"c-segmented--full"}
                        onChange={(value) => setMp3Available(value)}
                    />
                </div>
            </div>
        </div>
    );
});

PodcastEpisodeListTemplate.propTypes = {
    podcastEpisodeListViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default defaultTemplate(PodcastEpisodeListTemplate);
